<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Config } from '@/Config'
import * as Server from '@/services/Server'
import * as Alerts from '@/services/Alerts'
import * as Auth from '@/services/Auth'
import { refreshGameLink } from '@/services/GameLink'
import ConfirmDialog from '@/components/ConfirmDialog.vue'

/**
 * Enable/disable an event that requires an explicit activation (see racing_events_laptime.md,
 * "Stages"): the choice decides whether the game server offers the stage to this player — until
 * they ask for it the event never shows up in game, not even as a badge. The choice travels with
 * the next launch, which is why the launch link is refetched right after it and the dialog says
 * the game has to be reopened.
 *
 * It renders the dialog as well, and decides by itself whether it has anything to offer, out of
 * the event payload the caller already has: `requiresActivation` (the rule), `playable` (the
 * choice would go nowhere outside a running event) and this player's own `myActivation`. Written
 * that way the caller has no rule to duplicate — it just drops the component next to the play
 * button.
 *
 * The state is emitted as a boolean, so each caller applies it where it keeps its copy: the event
 * page in its detail, the play page in its card.
 */
defineOptions({ inheritAttrs: false })

const props = defineProps({
  /** The event as the API serves it (EventLib::detail or EventLib::present). */
  event: { type: Object, required: true },
  /**
   * Placement of the switch, not a rule of the event: the play-page card offers it only to ask
   * for the event (`true`), so once it is on the card has nothing left to show — turning it off
   * stays a choice of the event page, where the state of the whole event is in front of the
   * player. The default is the full toggle.
   */
  hideWhenActive: { type: Boolean, default: false },
})

const emit = defineEmits(['change'])

const { t } = useI18n()

// Same shortcut the event pages use: the toggle is for players who can act on it, and an
// anonymous payload has no activation to show in the first place.
const isAuthenticated = Auth.isAuthenticated()

const confirming = ref(false)
const saving = ref(false)

const active = computed(() => props.event.myActivation === true)

const canToggle = computed(() => isAuthenticated
  && props.event.requiresActivation === true
  && props.event.playable === true
  && (!props.hideWhenActive || !active.value))

async function apply() {
  saving.value = true

  try {
    const response = await Server.put(Config.server.routes.api.events_activation(props.event.id), {
      active: !active.value,
    })
    const updated = response.data?.data?.myActivation === true

    // The launch link is pre-fetched and refreshed on a timer: without this the next Play click
    // could still carry the previous choice (see GameLink.js).
    refreshGameLink()
    emit('change', updated)
    Alerts.success(t(updated ? 'events.activation.done' : 'events.activation.undone'))
  } catch (error) {
    Alerts.error(error.response?.data?.message ?? t('events.activation.error'))
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <v-btn
    v-if="canToggle"
    v-bind="$attrs"
    variant="tonal"
    :color="active ? 'success' : 'primary'"
    :prepend-icon="active ? 'mdi-toggle-switch' : 'mdi-toggle-switch-off-outline'"
    :loading="saving"
    class="w-100"
    @click="confirming = true"
  >
    {{ $t(active ? 'events.activation.deactivate' : 'events.activation.activate') }}
  </v-btn>

  <!-- Outside the condition above: the dialog has to stay mounted while it closes (see
       EventResetButton for the same pattern). -->
  <ConfirmDialog
    v-model="confirming"
    :title="$t(active ? 'events.activation.deactivateTitle' : 'events.activation.activateTitle')"
    :text="$t(active ? 'events.activation.deactivateText' : 'events.activation.activateText')"
    :confirm-text="$t(active ? 'events.activation.deactivateConfirm' : 'events.activation.activateConfirm')"
    :cancel-text="$t('common.cancel')"
    :confirm-color="active ? 'warning' : 'primary'"
    @confirm="apply"
  />
</template>
